import { FormEvent, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ActionsMenu from '../../components/common/ActionsMenu';
import Modal from '../../components/common/Modal';
import Pagination from '../../components/common/Pagination';
import { useAuth } from '../../contexts/AuthContext';
import { MY_SUBUSERS_QUERY, SUBUSER_PROJECTS_QUERY } from '../../graphql/queries';
import {
  INVITE_SUBUSER_MUTATION, REMOVE_SUBUSER_MUTATION, RESEND_SUBUSER_INVITE_MUTATION,
  SET_SUBUSER_ACTIVE_MUTATION, SET_SUBUSER_PROJECT_ROLE_MUTATION,
} from '../../graphql/mutations';
import { MEMBER_ROLES } from '../../utils/projectRole';
import styles from './TeamManagement.module.css';

interface Subuser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: 'invited' | 'active' | 'deactivated';
  invitedAt: string | null;
  lastLoginAt: string | null;
  projectCount: number;
}

interface SubuserProject { projectId: string; name: string; role: string | null }

const PAGE = 20;
const PROJECT_PAGE = 10;

const STATUS: Record<Subuser['status'], { label: string; className: string }> = {
  invited: { label: 'Invited', className: styles.statusInvited },
  active: { label: 'Active', className: styles.statusActive },
  deactivated: { label: 'Deactivated', className: styles.statusOff },
};

const day = (iso: string | null) => (iso
  ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : 'Never');

export const seatsText = (used: number, max: number): string =>
  (max > 0 ? `${used} of ${max} subusers` : `${used} subuser${used === 1 ? '' : 's'} · no limit on your plan`);

const ProjectAccess = ({ subuser, onClose }: { subuser: Subuser; onClose: () => void }) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const { data, loading } = useQuery(SUBUSER_PROJECTS_QUERY, {
    variables: { userId: subuser.id, page, limit: PROJECT_PAGE, search: search || undefined },
    fetchPolicy: 'cache-and-network',
  });
  const [setRole, { loading: saving }] = useMutation(SET_SUBUSER_PROJECT_ROLE_MUTATION, {
    refetchQueries: ['SubuserProjects', 'MySubusers'],
    onError: (err) => setError(err.message),
  });
  const rows: SubuserProject[] = data?.subuserProjects?.projects ?? [];
  const total: number = data?.subuserProjects?.total ?? 0;

  return (
    <Modal isOpen onClose={onClose} title={`Projects of ${subuser.firstName} ${subuser.lastName}`} maxWidth="max-w-2xl">
      <div className="space-y-3">
        <p className="text-sm text-on-surface-variant">
          Choose which of your projects {subuser.firstName} can open and what they may do there. Projects they are not on stay hidden from them.
        </p>
        <input
          className={styles.search}
          type="search"
          placeholder="Search projects…"
          aria-label="Search projects"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        {error && <p className={styles.error} role="alert">{error}</p>}
        {loading && rows.length === 0 ? (
          <p className="text-sm text-on-surface-variant">Loading projects…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-on-surface-variant">{search ? 'No project matches.' : 'You have no projects yet.'}</p>
        ) : (
          <ul className={styles.projectList}>
            {rows.map((p) => (
              <li key={p.projectId} className={styles.projectRow}>
                <span className={styles.projectName}>{p.name}</span>
                <select
                  aria-label={`Access to ${p.name}`}
                  value={p.role ?? ''}
                  disabled={saving}
                  onChange={(e) => {
                    setError('');
                    void setRole({ variables: { userId: subuser.id, projectId: p.projectId, role: e.target.value || null } });
                  }}
                >
                  <option value="">No access</option>
                  {MEMBER_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </li>
            ))}
          </ul>
        )}
        <Pagination page={page} pageSize={PROJECT_PAGE} total={total} onPageChange={setPage} noun="projects" variant="inline" />
        <dl className={styles.roleHelp}>
          {MEMBER_ROLES.map((r) => (
            <div key={r.value}><dt>{r.label}</dt><dd>{r.hint}</dd></div>
          ))}
        </dl>
      </div>
    </Modal>
  );
};

const TeamManagement = () => {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '' });
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const [accessFor, setAccessFor] = useState<Subuser | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Subuser | null>(null);

  const { data, loading, error } = useQuery(MY_SUBUSERS_QUERY, {
    variables: { page, limit: PAGE, search: search || undefined },
    fetchPolicy: 'cache-and-network',
    skip: !user || !!user.isSubuser,
  });
  const refetch = { refetchQueries: ['MySubusers'] };
  const failed = (err: Error) => setActionError(err.message);

  const [invite, { loading: inviting }] = useMutation(INVITE_SUBUSER_MUTATION, {
    ...refetch,
    onCompleted: (res) => {
      setInviteOpen(false);
      setForm({ email: '', firstName: '', lastName: '' });
      setNotice(`Invitation sent to ${res.inviteSubuser.email}. The link works for 7 days.`);
    },
    onError: (err) => setFormError(err.message),
  });
  const [resend] = useMutation(RESEND_SUBUSER_INVITE_MUTATION, {
    onCompleted: () => setNotice('Invitation sent again. The earlier link no longer works.'),
    onError: failed,
  });
  const [setActive] = useMutation(SET_SUBUSER_ACTIVE_MUTATION, { ...refetch, onError: failed });
  const [remove, { loading: removing }] = useMutation(REMOVE_SUBUSER_MUTATION, {
    ...refetch,
    onCompleted: (res) => { setRemoveTarget(null); setNotice(res.removeSubuser.message); },
    onError: (err) => { setRemoveTarget(null); failed(err); },
  });

  if (user?.isSubuser) return <Navigate to="/dashboard" replace />;

  const result = data?.mySubusers;
  const subusers: Subuser[] = result?.subusers ?? [];
  const total: number = result?.total ?? 0;
  const seatsUsed: number = result?.seatsUsed ?? 0;
  const maxSubusers: number = result?.maxSubusers ?? 0;
  const full = maxSubusers > 0 && seatsUsed >= maxSubusers;

  const submitInvite = (e: FormEvent) => {
    e.preventDefault();
    if (!form.email.trim() || !form.firstName.trim() || !form.lastName.trim()) { setFormError('Fill in the email and both names.'); return; }
    setFormError('');
    void invite({ variables: form });
  };

  return (
    <DashboardLayout onSearch={(q) => { setSearch(q); setPage(1); }} searchPlaceholder="Search your team...">
      <div className="space-y-6">
        <div className={styles.header}>
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Team Management</h2>
            <p className="text-on-surface-variant mt-1">
              People you invite sign in with their own email and password, use your plan and its limits, and see only the projects you give them.
            </p>
          </div>
          <div className={styles.headerSide}>
            <span className={full ? styles.seatsFull : styles.seats}>{result ? seatsText(seatsUsed, maxSubusers) : ' '}</span>
            <button
              className="btn-primary px-4 py-2.5 rounded-lg text-sm"
              disabled={full}
              title={full ? 'Your plan has no free seat — remove someone or upgrade the plan' : undefined}
              onClick={() => { setFormError(''); setInviteOpen(true); }}
            >
              <span className="material-symbols-outlined text-sm" aria-hidden="true">person_add</span>
              Invite subuser
            </button>
          </div>
        </div>

        {notice && <p className={styles.notice} role="status">{notice}</p>}
        {actionError && <p className={styles.error} role="alert">{actionError}</p>}

        {loading && !result ? (
          <p className="text-on-surface-variant">Loading your team…</p>
        ) : error ? (
          <p className={styles.error} role="alert">Could not load your team: {error.message}</p>
        ) : subusers.length === 0 ? (
          <div className={styles.empty}>
            <span className="material-symbols-outlined" aria-hidden="true">groups</span>
            <p>{search ? 'Nobody in your team matches.' : 'No subusers yet. Invite the people who work with you.'}</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl overflow-hidden">
            <div className={styles.tableWrap}>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container-low">
                    <th className="table-header">Person</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Projects</th>
                    <th className="table-header">Last login</th>
                    <th className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {subusers.map((s) => (
                    <tr key={s.id} className="table-row">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-on-surface">{s.firstName} {s.lastName}</p>
                        <p className="text-xs text-on-surface-variant">{s.email}</p>
                      </td>
                      <td className="px-6 py-4"><span className={STATUS[s.status].className}>{STATUS[s.status].label}</span></td>
                      <td className="px-6 py-4 text-sm text-on-surface-variant">{s.projectCount}</td>
                      <td className="px-6 py-4 text-xs text-on-surface-variant">{s.status === 'invited' ? `Invited ${day(s.invitedAt)}` : day(s.lastLoginAt)}</td>
                      <td className="px-6 py-4 text-right">
                        <ActionsMenu
                          label={`Actions for ${s.firstName} ${s.lastName}`}
                          actions={[
                            { label: 'Project access', icon: 'folder_shared', onSelect: () => setAccessFor(s) },
                            s.status === 'invited' && {
                              label: 'Send invitation again', icon: 'forward_to_inbox',
                              onSelect: () => { setActionError(''); void resend({ variables: { userId: s.id } }); },
                            },
                            {
                              label: s.status === 'deactivated' ? 'Activate' : 'Deactivate',
                              icon: s.status === 'deactivated' ? 'person_check' : 'person_off',
                              onSelect: () => { setActionError(''); void setActive({ variables: { userId: s.id, active: s.status === 'deactivated' } }); },
                            },
                            { label: 'Remove from team', icon: 'person_remove', danger: true, onSelect: () => setRemoveTarget(s) },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={PAGE} total={total} onPageChange={setPage} noun="subusers" />
          </div>
        )}
      </div>

      <Modal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite a subuser"
        footer={
          <>
            <button onClick={() => setInviteOpen(false)} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg">Cancel</button>
            <button type="submit" form="invite-form" disabled={inviting} className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50">
              {inviting ? 'Sending…' : 'Send invitation'}
            </button>
          </>
        }
      >
        <form id="invite-form" className="space-y-4" onSubmit={submitInvite}>
          <p className="text-sm text-on-surface-variant">
            They get an email with a link to choose a password (valid 7 days). An email that already has an Infrio account cannot be invited.
          </p>
          {formError && <p className={styles.error} role="alert">{formError}</p>}
          <label className={styles.field}>
            <span>Email</span>
            <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          </label>
          <div className={styles.fieldRow}>
            <label className={styles.field}>
              <span>First name</span>
              <input value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} />
            </label>
            <label className={styles.field}>
              <span>Last name</span>
              <input value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} />
            </label>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        title="Remove from team"
        footer={
          <>
            <button onClick={() => setRemoveTarget(null)} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg">Cancel</button>
            <button
              onClick={() => removeTarget && void remove({ variables: { userId: removeTarget.id } })}
              disabled={removing}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-error text-on-error hover:opacity-90 disabled:opacity-50"
            >
              {removing ? 'Removing…' : 'Remove'}
            </button>
          </>
        }
      >
        <p className="text-sm text-on-surface-variant">
          {removeTarget?.firstName} {removeTarget?.lastName} is signed out and taken off every project. Their seat is freed;
          what they uploaded stays in your projects. You can invite the same email again later.
        </p>
      </Modal>

      {accessFor && <ProjectAccess subuser={accessFor} onClose={() => setAccessFor(null)} />}
    </DashboardLayout>
  );
};

export default TeamManagement;
